import Typography from "@mui/material/Typography";
import { Link } from "react-router";

import { SlackChannelLink } from "@/shared/components/links/SlackChannelLink";
import {
  TARGET_TYPE_LABELS,
  TriggerTargetType,
  type TriggerDispatchRecord,
  type TriggerTargetSpec,
} from "@/shared/domain/triggers";
import { Goto } from "@/shared/navigation";

/**
 * Path to an action's page. Owner-qualified when the target pins one, which is
 * how a trigger references an action published by another org.
 */
function actionPath(name: string, ownerId?: string | null): string {
  const parts = ["/actions"];
  if (ownerId) {
    parts.push(encodeURIComponent(ownerId));
  }
  parts.push(encodeURIComponent(name));
  return parts.join("/");
}

interface DispatchTargetCellProps {
  dispatch: TriggerDispatchRecord;
  /**
   * The trigger's current targets, when the caller has them. Matched by
   * `target_id` to name what ran. Absent on listings that span many triggers.
   */
  targets?: readonly TriggerTargetSpec[];
}

/**
 * Which target a dispatch ran, named the way a person would name it: the action
 * invoked, the agent started, the Slack channel posted to.
 *
 * The dispatch row itself stores only `target_id` and `target_type`, so the
 * identity comes from the trigger's current targets when the caller has them.
 * When it does not — a listing spanning many triggers — or when the target has
 * since been removed from the trigger, the type is still worth saying; the
 * opaque id moves into the tooltip, where it is recoverable without being the
 * thing a reader has to decode.
 */
export function DispatchTargetCell({
  dispatch,
  targets,
}: DispatchTargetCellProps) {
  const target = targets?.find(
    (candidate) => candidate.target_id === dispatch.target_id,
  );
  const typeLabel = TARGET_TYPE_LABELS[dispatch.target_type];

  if (!target) {
    return (
      <Typography variant="body2" component="span" title={dispatch.target_id}>
        {typeLabel}
      </Typography>
    );
  }

  switch (target.type) {
    case TriggerTargetType.InvokeAction:
      return (
        <Link
          to={actionPath(target.action.name, target.action.owner)}
          title={dispatch.target_id}
        >
          {target.action.name}
        </Link>
      );
    case TriggerTargetType.StartAgent:
      return (
        <Link
          to={Goto.createAgentRoute(target.agent_id)}
          title={dispatch.target_id}
        >
          {target.agent_id}
        </Link>
      );
    case TriggerTargetType.SendSlackMessage:
      return <SlackChannelLink channelId={target.channel_id} />;
  }
}
